import { useState } from 'react'
import { useForm } from 'react-hook-form'

import { yupResolver } from '@hookform/resolvers/yup'
import { Select, ToggleSwitch } from '@ui/components/core'
import { DynamicFormProps } from '@ui/interfaces'
import { SaveAndCancelButtons } from '@ui/pages/pipelinesWorkflow/components/DestinationForms/SaveAndCancelButtons'
import * as Yup from 'yup'

export default function SigNozOtel(props: DynamicFormProps) {
  const schema = Yup.object({
    url: Yup.string().url().required('A URL for your SigNoz instance is required'),
    port: Yup.number().min(1).max(65536).required('A port must be provided, and must be a number'),
    'api-key': Yup.string().optional(),
    region: Yup.string().optional(),
    'signoz-cloud-enabled': Yup.boolean().default(false),
  }).required()

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
    watch,
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      port: 4317,
      ...props.config,
    },
  })

  const [usingSigNozCloud, setUsingSigNozCloud] = useState(props.config?.['signoz-cloud-enabled'] || false)

  register('region', { required: false })
  const watchRegion = watch('region', 'us')

  const toggleSigNozCloud = (checked: boolean) => {
    setUsingSigNozCloud(checked)
    configureForSigNozCloud(watchRegion || 'us', checked)
  }

  const configureForSigNozCloud = (watchRegion: string, enabled: boolean) => {
    if (enabled) {
      setValue('url', `https://ingest.${watchRegion}.signoz.cloud`)
      setValue('port', 443)
      setValue('region', watchRegion)
      setValue('signoz-cloud-enabled', true)
    } else {
      setValue('signoz-cloud-enabled', false)
    }
  }

  const { saveClick, cancelClick } = props
  const watchPort = watch('port', 4317)

  return (
    <form onSubmit={handleSubmit(saveClick)}>
      <div className="flex flex-col">
        {props.additionalInputs}
        <div className="mt-4">
          <div className="mt-2">
            <div className="flex items-center">
              <ToggleSwitch
                label={'Using SigNoz Cloud?'}
                checked={usingSigNozCloud}
                onChange={(value) => toggleSigNozCloud(value)}
              />
            </div>
          </div>
        </div>
        {!usingSigNozCloud && (
          <>
            <div className="mt-4">
              <label htmlFor="url" className="block text-sm font-medium leading-6 text-gray-900">
                URL*
              </label>
              <div className="mt-2">
                <div className="flex rounded-md shadow-sm ring-1 ring-inset ring-gray-300 focus-within:ring-2 focus-within:ring-inset focus-within:ring-indigo-600 sm:max-w-md">
                  <input
                    {...register('url')}
                    type="text"
                    id="url"
                    name="url"
                    className="block flex-1 border-0 bg-transparent py-1.5 pl-3 text-gray-900 placeholder:text-gray-400 focus:ring-0 sm:text-sm sm:leading-6"
                    placeholder="https://signoz.example.com"
                    disabled={props.disableInputs}
                  />
                  <span className="flex select-none items-center pr-3 text-gray-500 sm:text-sm">
                    :{watchPort || 4137}
                  </span>
                </div>
                {errors['url'] && <p className="mt-2 text-xs italic text-red-500">{errors['url'].message}</p>}
              </div>
            </div>
            <div className="mt-4">
              <label htmlFor="port" className="block text-sm font-medium leading-6 text-gray-900">
                Port*
              </label>
              <div className="mt-2">
                <div className="flex rounded-md shadow-sm ring-1 ring-inset ring-gray-300 focus-within:ring-2 focus-within:ring-inset focus-within:ring-indigo-600 sm:max-w-md">
                  <input
                    {...register('port')}
                    type="text"
                    id="port"
                    name="port"
                    className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                    placeholder="4317"
                    disabled={props.disableInputs}
                  />
                </div>
                {errors['port'] && <p className="mt-2 text-xs italic text-red-500">{errors['port'].message}</p>}
              </div>
            </div>
          </>
        )}
        {usingSigNozCloud && (
          <div className="mt-4">
            <label htmlFor="port" className="block text-sm font-medium leading-6 text-gray-900">
              Region
            </label>
            <div className="mt-2">
              <div className="flex w-full rounded-md shadow-sm ring-1 ring-inset ring-gray-300 focus-within:ring-2 focus-within:ring-inset focus-within:ring-indigo-600 sm:max-w-md">
                <Select
                  onChange={(value) => {
                    if (props.disableInputs) return
                    configureForSigNozCloud(value as string, usingSigNozCloud)
                  }}
                  options={['us', 'eu', 'in'].map((option) => ({
                    value: option,
                    label: option.toUpperCase(),
                  }))}
                  value={watchRegion}
                />
              </div>
              {errors['region'] && <p className="mt-2 text-xs italic text-red-500">{errors['region'].message}</p>}
            </div>
          </div>
        )}

        <div className="mt-4">
          <label htmlFor="api-key" className="block text-sm font-medium leading-6 text-gray-900">
            API Key
          </label>
          <div className="mt-2">
            <div className="flex rounded-md shadow-sm ring-1 ring-inset ring-gray-300 focus-within:ring-2 focus-within:ring-inset focus-within:ring-indigo-600 sm:max-w-md">
              <input
                {...register('api-key')}
                type="text"
                id="api-key"
                name="api-key"
                className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                placeholder="abc123"
                disabled={props.disableInputs}
              />
            </div>
            {errors['api-key'] && <p className="mt-2 text-xs italic text-red-500">{errors['api-key'].message}</p>}
          </div>
        </div>
      </div>
      {props.submitButton ? props.submitButton : <SaveAndCancelButtons cancelClick={cancelClick} />}
    </form>
  )
}
